import {
    AfterViewChecked,
    AfterViewInit,
    Component,
    ElementRef,
    HostListener,
    Input,
    OnDestroy,
    OnInit,
    ViewChild,
} from '@angular/core';
import { UntypedFormControl } from '@angular/forms';
import { MatSort } from '@angular/material/sort';
import { MatLegacyTableDataSource as MatTableDataSource } from '@angular/material/legacy-table';
import { Router } from '@angular/router';
import { Store } from '@ngrx/store';
import { BehaviorSubject, combineLatest, Observable, Subject } from 'rxjs';
import { filter, map, shareReplay, startWith, switchMap, takeUntil, tap } from 'rxjs/operators';
import { PartnerEntity } from '../../dave-data-module/entities/partner.entity';
import { PartnerOfficeEntity } from '../../dave-data-module/entities/partnerOffice.entity';
import { State } from '../../dave-data-module/State';
import { getPartnerOffices, getPartners } from '../../dave-data-module/State/selectors/partners.selectors';
import { getManagedUsers } from '../../dave-data-module/State/selectors/users.selectors';
import { BreakpointObserverService } from '../../dave-utils-module/dave-shared-components-module/services/breakpoint-observer.service';
import { PermissionService } from '../../dave-utils-module/dave-shared-components-module/services/permission.service';
import { isNotNullOrUndefined, TableColumnConfig } from '../../helper/helper';

interface TableData {
    Mitgliedernummer: string;
    Partner: PartnerEntity;
    PartnerMainOffice: PartnerOfficeEntity;
    Email: string;
    Telefonnummer: string;
}

@Component({
    selector: 'app-partner-list',
    templateUrl: './partner-list.component.html',
    styleUrls: ['./partner-list.component.scss'],
})
export class PartnerListComponent implements OnInit, OnDestroy, AfterViewChecked, AfterViewInit {
    @ViewChild('wrapperElement') WrapperElement: ElementRef;
    Mobile$: Observable<boolean>;
    public Mobile = false;
    @Input() ListStyle = 'partner';
    public SelectedId: number = null;
    public SearchForm: FormControlTyped<string> = new UntypedFormControl();

    /** Ob die Komponente unter einer bestimmten Breite ist */
    public IsSmallWidth$ = new BehaviorSubject(false);

    public ShowSearchBar$ = new BehaviorSubject(false);

    /** Konfiguration der Spalten der MatTable */
    public Columns: TableColumnConfig<TableData>[];

    /** Angezeigte Spalten der MatTable */
    public DisplayedColumns$: Observable<string[]>;

    /** Daten der Tabelle */
    public DataSource$: Observable<MatTableDataSource<TableData>>;

    /** Der `MatSort` der Tabelle */
    @ViewChild(MatSort) private matSort?: MatSort;

    /** Das Suchfeld-Element */
    @ViewChild('input') private inputRef?: ElementRef<HTMLInputElement>;

    /** Gibt ein Mal einen Wert aus, wenn der ngAfterViewInit Hook ausgelöst wird */
    private afterViewInit$ = new Subject<void>();
    private onDestroy$ = new Subject<void>();

    constructor(
        private store: Store<State>,
        private router: Router,
        public PS: PermissionService,
        private breakpointObserverService: BreakpointObserverService,
    ) {
        this.Mobile$ = breakpointObserverService.MobileQuery;
        this.Columns = [
            { header: 'Mitgliedernummer', name: 'Mitgliedernummer' },
            { header: 'Partner', name: 'Partner' },
            { header: 'E-Mail', name: 'Email' },
            { header: 'Telefonnummer', name: 'Telefonnummer' },
        ];

        this.DisplayedColumns$ = this.IsSmallWidth$.pipe(
            map(isTableSmallWidth =>
                isTableSmallWidth
                    ? ['Mitgliedernummer', 'Partner', 'chevron']
                    : ['Mitgliedernummer', 'Partner', 'Email', 'Telefonnummer', 'chevron'],
            ),
            shareReplay({ refCount: true, bufferSize: 1 }), // wird mehrmals subscribed
        );

        this.DataSource$ = combineLatest([
            this.store.select(getPartners).pipe(map(val => val.filter(c => !c.Deleted))),
            this.store.select(getPartnerOffices).pipe(filter(isNotNullOrUndefined)),
            this.store.select(getManagedUsers),
            this.afterViewInit$,
        ]).pipe(
            map(
                ([partners, partnerOffices, users]) =>
                    new MatTableDataSource(
                        partners.map<TableData>(partner => {
                            const user = users.filter(u => partner.Id === u.PartnerId);
                            const office = partnerOffices.find(p => partner.Id === p.PartnerId && p.MainOffice);

                            return {
                                Mitgliedernummer: partner.PartnerNo,
                                Partner: partner,
                                PartnerMainOffice: office,
                                Email: office?.Email,
                                Telefonnummer: office?.PhoneNumber,
                                User: user,
                                RouterLink: ['/partner', partner.Id],
                            };
                        }),
                    ),
            ),

            // this.matSort sollte hier immer gesetzt sein (da ngAfterViewInit gefeuert haben muss),
            // aber das weiß TypeScript natürlich nicht.
            tap(dataSource => this.matSort && (dataSource.sort = this.matSort)),

            // Der Standard-`sortingDataAccessor` kommt mit numerischen Strings - wie der
            // Vertragsnummer - nicht klar und sortiert nicht. Workaround:
            tap(
                dataSource =>
                    (dataSource.sortingDataAccessor = (object, key) => {
                        switch (key) {
                            case 'Partner': // ausnahme für kunden name
                                return `${object['Partner']['Name']}`.trim().toLowerCase();
                            case 'Mitgliedernummer': // ausnahme für kunden name
                                return `${object['Partner']['PartnerNo']}`.trim().toLowerCase();
                            default:
                                return object[key];
                        }
                    }),
            ),

            // Vergleichsfunktion zum Freitext-Filtern
            tap(
                dataSource =>
                    (dataSource.filterPredicate = (data, searchTerm) =>
                        [data.Mitgliedernummer, data.PartnerMainOffice?.Name, data.Email, data.Telefonnummer]
                            .map(value => `${value}`.trim().toLowerCase())
                            .some(value => value.includes(searchTerm))),
            ),

            // Sucheingaben an die DataSource weitergeben
            switchMap(dataSource =>
                this.SearchForm.valueChanges.pipe(
                    startWith(''),
                    tap(searchTerm => (dataSource.filter = searchTerm.trim().toLowerCase())),
                    map(() => dataSource),
                ),
            ),
        );
        // Seiteneffekt: wenn das Suchfeld eingeblendet wird, fokussieren - asynchron, damit Angular
        // das Suchfeld erst anzeigt und danach fokussiert. Wenn ausgeblendet, den Suchwert löschen.
        this.ShowSearchBar$.pipe(takeUntil(this.onDestroy$)).subscribe(isSearching =>
            isSearching
                ? setTimeout(() => this.inputRef && this.inputRef.nativeElement.focus())
                : this.SearchForm.setValue(''),
        );
    }

    ngAfterViewInit() {
        this.afterViewInit$.next();
    }

    ngOnInit() {
        if (this.router.url.indexOf('partner') > 0) {
            const idParts: string[] = this.router.url.substr(this.router.url.indexOf('partner')).split('/');
            if (!!idParts && idParts.length > 0) {
                this.SelectedId = +idParts[0];
            }
        }
    }

    ngOnDestroy() {
        this.onDestroy$.next();
    }

    @HostListener('window:resize')
    ngAfterViewChecked() {
        const smallWidth = this.WrapperElement?.nativeElement?.offsetWidth < 640;

        if (this.IsSmallWidth$.value !== smallWidth) {
            this.IsSmallWidth$.next(smallWidth);
        }
    }

    public FilterCustomColumns(c: any) {
        return !(c.name === 'Partner');
    }
}
